import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthProvider, AuthState } from './types.js';

interface AuthContextValue {
  state: AuthState;
  provider: AuthProvider;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthContextProviderProps {
  /** The AuthProvider instance that backs this context. Typically created once in main.tsx. */
  provider: AuthProvider;
  children: ReactNode;
}

/**
 * React context provider that bridges an AuthProvider instance into the
 * React tree. Subscribes to the provider's state changes on mount and
 * re-renders descendants whenever the state changes.
 *
 * Call `initialize()` on the provider is triggered here (with error logging)
 * so that the app doesn't have to remember to kick it off manually. This is
 * safe because `initialize()` is documented as idempotent.
 */
export function AuthContextProvider({
  provider,
  children,
}: AuthContextProviderProps): ReactNode {
  const [state, setState] = useState<AuthState>(() => provider.getState());

  useEffect(() => {
    let cancelled = false;

    provider.initialize().catch((err: unknown) => {
      if (!cancelled) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('Auth provider initialization failed:', message);
      }
    });

    const unsubscribe = provider.subscribe((next) => {
      if (!cancelled) {
        setState(next);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [provider]);

  return <AuthContext.Provider value={{ state, provider }}>{children}</AuthContext.Provider>;
}

/**
 * React hook returning the current auth state and the backing provider.
 * Throws if used outside an `AuthContextProvider`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthContextProvider>');
  }
  return ctx;
}
